import { useEffect, useState } from "react";

import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import { AdminRoute, ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ReportsProvider } from "@/hooks/useReports";
import { ForceChangePassword } from "@/pages/auth/ForceChangePassword";
import { ForgotPassword } from "@/pages/auth/ForgotPassword";
import { Login } from "@/pages/auth/Login";
import { TotpSetup } from "@/pages/auth/TotpSetup";
import { TwoFactor } from "@/pages/auth/TwoFactor";
import { ResetPassword } from "@/pages/auth/ResetPassword";
import { ReportCatalog } from "@/pages/ReportCatalog";
import { NotBuiltYet } from "@/pages/NotBuiltYet";
import { ReportAccess } from "@/pages/ReportAccess";
import { UserManagement } from "@/pages/UserManagement";
import { CompanyManagement } from "@/pages/CompanyManagement";

/** Ask the server whether the Microsoft button should exist at all. */
function useSsoConfigured(): boolean {
  const [configured, setConfigured] = useState(false);
  useEffect(() => {
    fetch("/internal/v1/health")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { ssoConfigured?: boolean } | null) => setConfigured(d?.ssoConfigured === true))
      .catch(() => setConfigured(false));
  }, []);
  return configured;
}

/**
 * The sign-in chain.
 *
 * ⚠ These steps are NOT routes, deliberately. The server's temp token is what authorises
 * each one, so the step the user is at is a property of the session and not of the URL.
 * Making them routes would let somebody navigate to /2fa directly — harmless, since the
 * server would refuse them, but it would show a screen that cannot work and looks broken.
 */
function AuthGate() {
  const { state } = useAuth();
  const ssoConfigured = useSsoConfigured();

  switch (state.status) {
    case "loading":
      // Blank rather than a spinner: the refresh call is one round trip to the same
      // origin, and a spinner that flashes for 80ms reads as jank.
      return null;
    case "password_expired":
      return <ForceChangePassword />;
    case "totp_setup":
      return <TotpSetup />;
    case "totp_verify":
      return <TwoFactor />;
    case "unauthenticated":
      return (
        <Routes>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Login ssoConfigured={ssoConfigured} />} />
        </Routes>
      );
    case "authenticated":
      return <AppRoutes />;
  }
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<ReportCatalog />} />
        <Route path="/reports/:key" element={<NotBuiltYet what="This report" chunk="a later chunk" />} />
        <Route
          path="/admin/users"
          element={<AdminRoute><UserManagement /></AdminRoute>}
        />
        <Route
          path="/admin/access"
          element={<AdminRoute><ReportAccess /></AdminRoute>}
        />
        <Route
          path="/admin/companies"
          element={<AdminRoute><CompanyManagement /></AdminRoute>}
        />
      </Route>
      {/* A signed-in user landing on /reset-password or an unknown path goes home. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ReportsProvider>
          <AuthGate />
          <Toaster position="top-right" richColors closeButton />
        </ReportsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
